import { BreakpointState } from "@angular/cdk/layout";
import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";

import { MOBILE_SIZE, DESKTOP_SIZE } from "@hermes/aphrodite/dynamic-box";
import {
    Product,
    ProductBuckleVariant,
    ProductColorVariant,
    ProductTemplateType,
} from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import {
    areAllSizeVariantsOutOfStock,
    hasSizeVariant,
    PageProductImageMediaqueries,
} from "@hermes/fragments/product-utils";
import {
    DESKTOP_SIZE_REVAMP,
    MOBILE_SIZE_REVAMP,
    ProductVariant,
    TABLET_SIZE_REVAMP,
} from "@hermes/fragments/variant-item";
import {
    BUCKLE,
    COLOR,
    FINISH,
    LEATHER,
    ProductPageFacade,
    SIZE,
    STRAP,
    STRAP_SIZE,
    VOLUME,
} from "@hermes/states/product-page";

import { skuToId } from "@hermes/utils-generic/helpers";
import { ImageUrlPipe } from "@hermes/utils-generic/pipes/image-url";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

// TODO: strict mode has been quickly fixed, should be revised and improved if needed

/**
 * This component manages a list of variants grid.
 *
 * Cuts in rows and add a button to display more
 */
@Component({
    selector: "h-product-variants",
    templateUrl: "./product-variants.component.html",
    styleUrls: ["./product-variants.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductVariantsComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public parentForm!: FormGroup;

    /**
     * Variant type.
     */
    @Input()
    public type!:
        | typeof COLOR
        | typeof SIZE
        | typeof VOLUME
        | typeof BUCKLE
        | typeof LEATHER
        | typeof FINISH
        | typeof STRAP
        | typeof STRAP_SIZE;

    /**
     * Full list of variants.
     */
    @Input()
    public variants: ProductVariant[] = [];

    /**
     * Current product.
     */
    @Input()
    public currentProduct!: Product;

    /**
     * Id of the element that describes the radio group.
     */
    @Input()
    public labelledById!: string;

    /**
     * If the user as selected a variant, will be provided by parent.
     * The value is not the sku, but the functional value :
     * - REF_SIZE for size variants
     * - HERMES_COLOR_CODE for color variants
     * - MASS_VOLUME for volume variants.
     * - ItemSku for strap variants
     */
    @Input()
    public selectedValue!: string;

    /** Activate or not the hiding of more variants. */
    @Input()
    public moreVariantsEnabled: boolean = false;

    @Input()
    public isThinBorder: boolean = false;

    @Input()
    public numberOfLinesToDisplay: number = 2;
    /**
     * initial shown variants.
     */
    public initialVariants: ProductVariant[] = [];

    /**
     * Hidden variants.
     */
    public hiddenVariants: ProductVariant[] = [];

    /** Number of hidden variants needed for A11Y and i18n  */
    public moreVariants: string | undefined;

    /** Will become true when user has clicked the show more button. */
    public showAllVariants: boolean = false;

    public imageSizeOnMobile = MOBILE_SIZE;

    public imageSizeOnDesktop = DESKTOP_SIZE;

    public imageSizeOnTablet = TABLET_SIZE_REVAMP;

    /** Enum of media queries for availability in the template. */
    public pageProductImageMediaqueries = PageProductImageMediaqueries;

    /**
     * Boolean to know if the current product possesses size variants
     * (used in conjunction with the areAllSizeVariantsOutOfStock property for products with color and size variations)
     */
    public hasSizeVariants: boolean = false;

    /**
     * Boolean to indicate if all size variations are out of stock
     * (used to change style for color variations for products with both color and size variants)
     */
    public areAllSizeVariantsOutOfStock: boolean = false;

    public itemsPerLine = 4;

    private subscription = new Subscription();

    constructor(
        private context: Context,
        private changeDetector: ChangeDetectorRef,
        public productPageFacade: ProductPageFacade,
        @Inject(DOCUMENT) private document: Document,
        private imageUrlPipe: ImageUrlPipe,
        private breakpointService: BreakpointService,
    ) {}

    public get thumbnailSize(): number {
        return this.getImageSize(DESKTOP_SIZE_REVAMP, MOBILE_SIZE);
    }

    public get isProductRevampSelectorEnabled(): boolean {
        return this.productPageFacade.isProductRevampSelectorEnabled(
            this.currentProduct,
        );
    }

    /**
     * Init selector form
     * Subscribe from store to know which variant is selected
     */
    public ngOnInit(): void {
        if (
            this.context.isInBrowserMode() &&
            !this.productPageFacade.isProductRevampSelectorEnabled(
                this.currentProduct,
            )
        ) {
            this.subscription.add(
                this.breakpointService
                    .mediumBreakpointObserver()
                    .subscribe((value: BreakpointState) => {
                        const isMobile = !value.matches;
                        this.itemsPerLine = isMobile ? 4 : 5;
                        this.recomputeHiddenVariants();
                    }),
            );
        }
        this.hasSizeVariants = hasSizeVariant(this.currentProduct);
        this.areAllSizeVariantsOutOfStock =
            this.hasSizeVariants &&
            areAllSizeVariantsOutOfStock(this.currentProduct);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["variants"] && changes?.["variants"]?.currentValue) {
            this.recomputeHiddenVariants();
            this.imageSizeOnMobile = this.getImageSize(
                MOBILE_SIZE_REVAMP,
                MOBILE_SIZE,
            );
            this.imageSizeOnDesktop = this.getImageSize(
                DESKTOP_SIZE_REVAMP,
                DESKTOP_SIZE,
            );
            this.imageSizeOnTablet = this.getImageSize(
                TABLET_SIZE_REVAMP,
                MOBILE_SIZE,
            );
        }
    }

    public isVariantWithImage(
        variant: ProductVariant,
    ): variant is ProductBuckleVariant | ProductColorVariant {
        return Object.prototype.hasOwnProperty.call(variant, "image");
    }

    /**
     * Trigger on changes of variants inputs
     * Calculate hidden variants depending
     * - show-more enabled
     * - to many variants to display or not
     */
    public recomputeHiddenVariants(): void {
        const new_displayed_variants = [...this.variants];

        // total of initail variants depends on numberOfLinesToDisplay and isProductRevampSelectorEnabled flag
        const totalInitialVariants =
            this.productPageFacade.isProductRevampSelectorEnabled(
                this.currentProduct,
            )
                ? this.numberOfLinesToDisplay * (this.itemsPerLine + 1)
                : this.itemsPerLine * this.numberOfLinesToDisplay;

        // Split in one or two arrays if there's too much and moreVariantsEnabled is true.
        if (
            this.moreVariantsEnabled &&
            this.variants.length > totalInitialVariants
        ) {
            this.initialVariants = new_displayed_variants.slice(
                0,
                totalInitialVariants,
            );
            this.hiddenVariants =
                new_displayed_variants.slice(totalInitialVariants);
            this.moreVariants = `${this.hiddenVariants.length + 1}`;
        } else {
            this.initialVariants = new_displayed_variants;
            this.hiddenVariants = [];
            this.moreVariants = undefined;
        }
        this.changeDetector.detectChanges();
    }

    /** Open the rest of variants. */
    public toggleHasMore(): void {
        if (
            this.productPageFacade.isProductRevampSelectorEnabled(
                this.currentProduct,
            )
        ) {
            if (this.currentProduct.variants) {
                this.productPageFacade.showMoreVariantsRequested({
                    variantItems: this.initialVariants,
                    variantType: this.type,
                    productType: ProductTemplateType.Simple,
                });
            }
            return;
        }
        this.showAllVariants = true;
        this.productPageFacade.showMoreVariant({
            id: this.type,
            isExpanded: true,
        });
        this.focusFirstMoreElement();
    }

    /**
     * When Show more is active, focus first element in the new line (A11Y)
     */
    public focusFirstMoreElement(): void {
        if (this.context.isInBrowserMode()) {
            const indexElement = this.initialVariants.length - 1;
            const element = this.document.getElementById(
                `${this.type}${indexElement}`,
            );
            if (element) {
                element.querySelector("input")?.focus();
            }
        }
    }

    /**
     * tracking function for *ngFor performance
     *
     * this is needed because every time a variant is selected the product and its variants are refreshed.
     * it allows angular to detect changes by comparing array items by values instead of rerendering all the array.
     */
    public trackBySKU(_index: number, variant: ProductVariant): string {
        // track by image if available to avoid reloading them if url don't change
        if ("image" in variant) {
            return variant.image;
        }
        return skuToId(variant.sku);
    }

    /**
     * Logic rules for when to show the show-more button
     */
    public shouldWrapWithShowMore(index: number): boolean {
        return (
            this.moreVariantsEnabled &&
            index + 1 === this.initialVariants.length && // button is the last of initial variants
            this.hiddenVariants.length > 0 && // there are more variants to display.
            !this.showAllVariants // show more variants are hidden
        );
    }

    /** Generate the scene7 url
     *
     * @param size : height and width of scene7 image
     * @param url : url of image
     * @param sourceSet : specifies the URL of the image to use in different situations
     */
    public getScene7Url(size: number, url: string, sourceSet: boolean): string {
        return this.imageUrlPipe.transform(
            url,
            {
                width: size,
                height: size,
                quality: "99,1",
                sourceSet,
            },
            true,
        );
    }

    /**
     * Return true if app is in server mode
     */
    public isInServerMode(): boolean {
        return this.context.isInServerMode();
    }

    public getSkuToId(sku: string): string {
        return skuToId(sku);
    }

    private getImageSize(sizeRevamp: number, sizeNorevamp: number): number {
        return this.productPageFacade.isProductRevampSelectorEnabled(
            this.currentProduct,
        )
            ? sizeRevamp
            : sizeNorevamp;
    }
}
